import React, { useEffect, useRef } from 'react';
import { motion, useInView, useSpring } from 'framer-motion';

const MetricDisplay = ({ value, label, unit, decimals = 0, delay = 0, title, IconComponent, exactValue }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  const springValue = useSpring(0, {
    damping: 100,
    stiffness: 1000,
    mass: 1,
  });

  useEffect(() => {
    if (isInView) {
      const animationTimeout = setTimeout(() => {
        springValue.set(value);
      }, delay);
      return () => clearTimeout(animationTimeout);
    }
  }, [isInView, value, springValue, delay]);

  useEffect(() => {
    const unsubscribe = springValue.on("change", (latest) => {
      if (ref.current) {
        const formatted = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(latest);

        ref.current.textContent = formatted;

      }
    });
    return () => unsubscribe();
  }, [springValue, decimals]);

  // Formatear el valor pequeño (ahora los millones) con 1 decimal
  const formattedSmall = exactValue 
    ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(exactValue) 
    : null;

  return (
    <motion.div
      className="flex flex-col items-center text-center p-6 bg-white/100 backdrop-blur-lg rounded-3xl shadow-xl border border-gray-200/50"
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.8, ease: "easeOut", delay: delay / 1000 }}
    >
      {IconComponent && (
        <motion.div
          className="w-16 h-16 mb-4 flex items-center justify-center text-orange-500"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, ease: "easeOut", delay: delay / 1000 + 0.1 }}
        >
          <IconComponent className="w-full h-full" />
        </motion.div>
      )}
      {title && (
        <motion.h3
          className="text-2xl font-bold text-gray-600 mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut", delay: delay / 1000 + 0.1 }}
        >
          {title}
        </motion.h3>
      )}
      <div className="flex flex-col items-center mb-2">
        <div className="flex items-baseline">
          <motion.span
            ref={ref}
            className="font-extrabold text-4xl md:text-5xl bg-gradient-to-r from-orange-500 to-orange-700 bg-clip-text text-transparent leading-none"
            initial={{ filter: "blur(10px)" }}
            animate={isInView ? { filter: "blur(0px)" } : {}}
            transition={{ duration: 1.2, ease: "easeOut", delay: delay / 1000 + 0.2 }}
          >
            0
          </motion.span>
          <motion.span
            className="ml-2 text-2xl md:text-3xl font-light text-orange-600"
            initial={{ opacity: 0, x: -10 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, ease: "easeOut", delay: delay / 1000 + 0.8 }}
          >
            {unit}
          </motion.span>
        </div>
        
        {/* Etiqueta de Millones (Texto pequeño, ahora dinámico) */}
        {formattedSmall && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs font-mono text-orange-400 mt-1 tracking-widest font-bold"
          >
            {formattedSmall} Million Hrs.
          </motion.div>
        )}
      </div>
      <motion.p
        className="text-lg md:text-xl text-gray-400 font-medium tracking-wide leading-relaxed max-w-xs"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: "easeOut", delay: delay / 1000 + 0.5 }}
      >
        {label}
      </motion.p>
    </motion.div>
  );
};

export default MetricDisplay;